/**
 * Customer Badge
 *
 * Renders an attached customer as a TagBadge ("Name · OrgName" when linked).
 * For superadmins, adds a small "link" affordance that opens a popover to set
 * or clear the linked organization.
 *
 * @module features/tasks/components/CustomerBadge
 */

import React from 'react';
import {
  Button,
  ComboBox,
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TagBadge,
  Text,
  hashStringToTagColor,
  useToast,
  type ComboBoxOption,
} from '@chatsdet/ui';
import { Link as LinkIcon, Buildings, X } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../../contexts/AuthContext';
import { useOrganizationsQuery } from '../../../ui/pages/superadmin/useSuperadminData';
import { useRemoveCustomer, useUpdateCustomer } from '../hooks/useTasks';
import type { AgentTaskCustomer } from '../types';

interface CustomerBadgeProps {
  taskId: string;
  customer: AgentTaskCustomer;
  canEdit: boolean;
}

export function CustomerBadge({ taskId, customer, canEdit }: CustomerBadgeProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperadmin = user?.isSuperadmin === true;
  const removeCustomer = useRemoveCustomer();
  const updateCustomer = useUpdateCustomer();
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);

  // Only superadmins can edit the org link (org directory is gated to them).
  const canEditLink = canEdit && isSuperadmin;

  // Account-linked customer: a clickable chip that opens the account on the
  // Accounts board. (Legacy free-text/org customers fall through below.)
  if (customer.accountCardId) {
    const accountCardId = customer.accountCardId;
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card py-0.5 pl-2 pr-1 text-xs font-medium">
        <button
          type="button"
          onClick={() =>
            void navigate({ to: '/boards/accounts', search: { cardId: accountCardId } })
          }
          className="inline-flex items-center gap-1 transition-colors hover:text-primary"
        >
          <Buildings size={11} weight="bold" className="text-muted-foreground" />
          {customer.name}
        </button>
        {canEdit && (
          <button
            type="button"
            aria-label={`Remove ${customer.name}`}
            onClick={(e) => {
              e.stopPropagation();
              removeCustomer.mutate({ taskId, customerId: customer.id });
            }}
            className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X size={10} weight="bold" />
          </button>
        )}
      </span>
    );
  }

  const label = customer.linkedOrgName
    ? `${customer.name} · ${customer.linkedOrgName}`
    : customer.name;

  const badge = (
    <TagBadge
      name={label}
      color={hashStringToTagColor(customer.name)}
      size="sm"
      onRemove={
        canEdit ? () => removeCustomer.mutate({ taskId, customerId: customer.id }) : undefined
      }
    />
  );

  if (!canEditLink) return badge;

  return (
    <Flex align="center" gap="xs">
      {badge}
      <Popover isOpen={open} onOpenChange={setOpen} placement="bottom start">
        <PopoverTrigger>
          <Button
            variant="ghost"
            size="sm"
            className="w-5 p-0"
            aria-label={
              customer.orgId ? 'Edit linked organization' : 'Link organization to customer'
            }
          >
            <LinkIcon size={12} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-3 w-[280px]">
          <CustomerLinkEditor
            customer={customer}
            onSubmit={(orgId) => {
              updateCustomer.mutate(
                { taskId, customerId: customer.id, customerOrgId: orgId },
                {
                  onSuccess: () => {
                    toast.success(orgId ? 'Customer linked' : 'Customer unlinked');
                    setOpen(false);
                  },
                  onError: () => toast.error('Failed to update link'),
                }
              );
            }}
            isPending={updateCustomer.isPending}
          />
        </PopoverContent>
      </Popover>
    </Flex>
  );
}

function CustomerLinkEditor({
  customer,
  onSubmit,
  isPending,
}: {
  customer: AgentTaskCustomer;
  onSubmit: (orgId: string | null) => void;
  isPending: boolean;
}) {
  const [inputValue, setInputValue] = React.useState('');
  const orgsQuery = useOrganizationsQuery({ search: inputValue, pageSize: 20 });

  const options: ComboBoxOption[] = React.useMemo(() => {
    const orgs = orgsQuery.data?.organizations ?? [];
    return orgs.map((o) => ({ id: o.id, label: o.name, textValue: o.name }));
  }, [orgsQuery.data]);

  return (
    <Flex direction="column" gap="sm">
      <Text size="sm" weight="medium">
        Link &ldquo;{customer.name}&rdquo; to an organization
      </Text>
      <ComboBox
        items={options}
        placeholder="Search organizations..."
        inputValue={inputValue}
        onInputChange={setInputValue}
        onSelectionChange={(key) => {
          const id = key as string | null;
          if (id) onSubmit(id);
        }}
        menuTrigger="focus"
      />
      {customer.orgId && (
        <Button
          variant="outline"
          size="sm"
          isDisabled={isPending}
          onPress={() => onSubmit(null)}
        >
          Clear link
        </Button>
      )}
    </Flex>
  );
}
